import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ApiError {
  code: number;
  message: string;
}

export interface ApiResponse<T> {
  status: 'success' | 'error';
  data?: T;
  error?: ApiError;
}

export interface DeviceDoc {
  id?: string; // device id; must be full CouchDB id or bare uuid
  label?: string; // user-friendly label
  description?: string;
  theme?: string;
  primaryColor?: string;
  darkMode?: boolean;
  systemZoomFactor?: number;
  editorZoomFactor?: number;
  homeView?: string;
  [k: string]: any;
}

@Injectable({ providedIn: 'root' })
export class DeviceManagementApiService {
  // Prefer env URL, but fall back to same-origin /api when env is missing
  private base = `${environment.apiBaseUrl || '/api'}/devices`;

  constructor(private http: HttpClient) {}

 /**
 * Builds HTTP options with Authorization header if an access token is present.
 *
 * @returns HTTP options containing Bearer token headers, or an empty object if unauthenticated.
 */
  private authOptions() {
    const token = localStorage.getItem('access_token');
    if (token) {
      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
      return { headers };
    }
    return {};
  }

/**
 * Ensures a device document ID has the required `device:` prefix.
 *
 * @param id Raw or prefixed device ID.
 * @returns Prefixed device ID, or the original value if empty.
 */
  private withPrefix(id: string): string {
    if (!id) return id;
    return id.startsWith('device:') ? id : `device:${id}`;
  }

/**
 * Retrieves all device documents from the server.
 *
 * @returns API response containing a list of device documents.
 */
  list(): Observable<ApiResponse<DeviceDoc[]>> {
    return this.http.get<ApiResponse<DeviceDoc[]>>(this.base, this.authOptions());
  }

/**
 * Retrieves a single device document from the server by ID.
 *
 * @param id Device identifier (with or without prefix).
 * @returns API response containing the device document.
 */
  get(id: string): Observable<ApiResponse<DeviceDoc>> {
    const device_id = this.withPrefix(id);
    return this.http.get<ApiResponse<DeviceDoc>>
        (`${this.base}/${encodeURIComponent(device_id)}`, this.authOptions());
  }

/**
 * Creates a new device document on the server.
 *
 * @param doc Partial device document to create.
 * @returns API response containing the created device document.
 */
  create(doc: Partial<DeviceDoc> = {}): Observable<ApiResponse<DeviceDoc>> {
    return this.http.post<ApiResponse<DeviceDoc>>(this.base, doc, this.authOptions());
  }

/**
 * Updates an existing device document on the server.
 *
 * @param id Device identifier (with or without prefix).
 * @param updates Fields to update on the device document.
 * @returns API response containing the updated device document.
 */
  update(
    id: string,
    updates: Partial<DeviceDoc> & { status?: 'active' | 'deleted' },
  ): Observable<ApiResponse<DeviceDoc>> {
    const did = this.withPrefix(id);
    return this.http.put<ApiResponse<DeviceDoc>>(
      `${this.base}/${encodeURIComponent(did)}`,
      updates as any,
      this.authOptions(),
    );
  }

/**
 * Deletes a device document on the server.
 *
 * @param id The device identifier (without prefix).
 * @returns API response indicating success or failure.
 */
  delete(id: string): Observable<ApiResponse<{}>> {
    const did = this.withPrefix(id);
    return this.http.delete<ApiResponse<{}>>(`${this.base}/${encodeURIComponent(did)}`, 
        this.authOptions());
  }
}
